<style rel="stylesheet/scss" lang="scss">
  @import url('https://fonts.googleapis.com/css?family=Roboto:300,400,700&display=swap');

  .leaflet-map-osrm {
    display: block;
    position: absolute;
    height: calc(100% - 30px);
    max-height: calc(100% - 30px);
    width: 350px;
    top: 15px;
    right: 15px;
    overflow: hidden;
    z-index: calc(var(--map-z-index) + 3);
    box-shadow: 0 0 27px -5px rgba(0,0,0,0.5);
    background-color: rgba(255,255,255,1);

    * {
      box-sizing: border-box;
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      font-weight: 400;
    }

    *:focus {
      outline: 0;
    }

    &__content {
      display: block;
      position: relative;
      height: 100%;
      color: #333;

      &__header {
        display: block;
        position: sticky;
        top: 0;
        width: 100%;
        height: 55px;
        border-bottom: 1px solid rgba(200,200,200,1);
        background-color: rgba(255,255,255,1);
        z-index: calc(var(--map-z-index) + 4);

        &__content {
          display: block;
          width: 100%;
          height: 55px;
          padding: 0 12px;
          line-height: 55px;
          font-size: 1.45em !important;
          font-weight: 300 !important;
          color: var(--map-accent-color);

          &__buttons {
            display: inline-block;
            float: right;
            text-align: left;

            &__close,
            &__print {
              position: relative;
              height: 55px;
              width: 36px;
              border: none;
              background: none;
              cursor: pointer;
              -webkit-appearance: none;
              -moz-appearance: none;
              -ms-appearance: none;
              -o-appearance: none;
              appearance: none;

              svg {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 16px;
                fill: var(--map-accent-color);
                transition : fill 150ms ease-out;
              }

              &:hover {
                svg {
                  fill: #333333;
                }
              }
            }
          }
        }
      }

      &__scroll {
        display: block;
        width: 100%;
        height: calc(100% - 55px);
        overflow-x: hidden;
        overflow-y: auto;

        &__leg {
          &__step {
            display: block;
            width: 100%;
            border-bottom: 1px solid rgba(200,200,200,0.6);
            padding: 10px 15px 10px 10px;

            &--arrive {
              background-color: rgba(200,200,200,0.6);
            }

            &__icon {
              display: inline;
              float: left;
              width: 36px;
              height: 36px;
              padding: 0 3px;
            }

            &__directions {
              display: inline;
              float: left;
              width: calc(100% - 52px);
              min-height: 36px;
              padding-left: 4px;
            }

            &__distance {
              display: block;
              clear: left;
              width: 100%;
              min-height: 1px;
              text-align: right;
              font-size: 0.8em !important;
              font-style: italic;
              padding-top: 4px;
            }
          }
        }
      }
    }
  }
</style>

<template>
  <div
    v-if="routeData"
    ref="container"
    class="leaflet-map-osrm"
    :style="baseStyles">

    <div class="leaflet-map-osrm__content">

      <span class="leaflet-map-osrm__content__header">
        <span class="leaflet-map-osrm__content__header__content">
          Driving Directions
          <span class="leaflet-map-osrm__content__header__content__buttons">
            <button
              @click="handleClose"
              class="leaflet-map-osrm__content__header__content__buttons__close">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 512"><path d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"/></svg>
            </button>
            <button
              @click="handlePrint"
              class="leaflet-map-osrm__content__header__content__buttons__print">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M448 192V77.25c0-8.49-3.37-16.62-9.37-22.63L393.37 9.37c-6-6-14.14-9.37-22.63-9.37H96C78.33 0 64 14.33 64 32v160c-35.35 0-64 28.65-64 64v112c0 8.84 7.16 16 16 16h48v96c0 17.67 14.33 32 32 32h320c17.67 0 32-14.33 32-32v-96h48c8.84 0 16-7.16 16-16V256c0-35.35-28.65-64-64-64zm-64 256H128v-96h256v96zm0-224H128V64h192v48c0 8.84 7.16 16 16 16h48v96zm48 72c-13.25 0-24-10.75-24-24 0-13.26 10.75-24 24-24s24 10.74 24 24c0 13.25-10.75 24-24 24z"/></svg>
            </button>
          </span>
        </span>
      </span>

      <div
        class="leaflet-map-osrm__content__scroll">

        <span
          v-for="leg in directions"
          class="leaflet-map-osrm__content__scroll__leg">

          <span
            v-for="step in leg"
            :class="getStepClass(step.type)">

            <img
              :src="`https://leaflet.rewn.io/images/${step.icon}.svg`"
              alt="direction icon"
              class="leaflet-map-osrm__content__scroll__leg__step__icon" />

            <span class="leaflet-map-osrm__content__scroll__leg__step__directions">

              <span
                class="leaflet-map-osrm__content__scroll__leg__step__directions__text">
                {{ step.instructions }}
              </span>

            </span>

            <span
              v-if="notEmpty(step.distance) && notEmpty(step.duration)"
              class="leaflet-map-osrm__content__scroll__leg__step__distance">
              {{ step.distance | m2m }} / {{ step.duration | s2t }}
            </span>

            <span
              v-else
              class="leaflet-map-osrm__content__scroll__leg__step__distance">
            </span>

          </span>

        </span>

      </div>

    </div>

  </div>
</template>

<script lang="js">
  import Vue from 'vue';
  import colorSelector from '../utils/colorSelector.js';
  import osrm from '../services/osrm';
  import has from 'lodash/has';
  import get from 'lodash/get';
  import polyline from '@mapbox/polyline';
  import {s2t, m2m} from '../utils/transformers';
  import PRINT_STYLES from '../lib/constents/printStyles';
  import notEmpty from '../utils/notEmpty';

  // import TEST_DATA from '../lib/constents/testData.json';

  export default Vue.extend({
    name: 'osrm',
    props: {
      waypoints: {
        type: Array,
        default: () => [],
      },
      zIndex: {
        type: Number,
        default: () => 9995,
      },
      clusterColor: {
        type: String,
        default: () => '8A99A8',
      },
    },
    filters: {
      s2t,
      m2m,
    },
    data() {
      return {
        routeData: null,
      };
    },
    computed: {
      wayPointBlob() {
        return this.waypoints
          .map(geo => `${geo.location.lon},${geo.location.lat}`)
          .join(';');
      },
      legs() {
        if (!has(this.routeData, 'routes')) {
          return null;
        }
        if (!this.routeData.routes.length) {
          return null;
        }
        return this.routeData.routes[0].legs;
      },
      geometry() {
        if (!has(this.routeData, 'routes')) {
          return null;
        }
        if (!this.routeData.routes.length) {
          return null;
        }
        const geometry = polyline.decode(this.routeData.routes[0].geometry, 5);
        return geometry.length ? geometry : [];
      },
      directions() {
        const directions = [];
        if (this.legs.length) {
          this.legs.forEach(leg => {
            const steps = [];
            leg.steps.forEach(step => {
              const name = get(step, 'name');
              const distance = get(step, 'distance');
              const duration = get(step, 'duration');
              const destinations = get(step, 'destinations');
              const ref = get(step, 'ref');
              const exits = get(step, 'exits');
              const type = get(step, 'maneuver.type');
              const modifier = get(step, 'maneuver.modifier');
              const icon = this.getDirectionIcon({type, modifier});
              let instructions = this.getInstructions({type, modifier, name, destinations, ref, exits});
              steps.push({distance, duration, instructions, icon, type});
            });
            directions.push(steps);
          });
        }
        return directions;
      },
      baseStyles() {
        return {
          '--map-accent-color': `#${this.clusterColor}`,
          '--map-accent-dif-color': colorSelector(this.clusterColor),
          '--map-z-index': this.zIndex,
        };
      },
    },
    methods: {
      notEmpty,
      handleClose() {
        this.$emit('closeOSRM');
      },
      handlePrint() {
        const print = window.open('', 'Print', 'height=600,width=800');
        print.document.write('<html><head><title>Print</title>');
        print.document.write(PRINT_STYLES);
        print.document.write('</head><body><div class="osrm-print">');
        this.directions.forEach(leg => {
          leg.forEach(step => {
            const icon = `https://leaflet.rewn.io/images/${step.icon}.svg`;
            print.document.write('<div class="osrm-print__section">');
            print.document.write(`<img class="osrm-print__section__icon" src="${icon}" alt="direction icon" />`);
            print.document.write('<div class="osrm-print__section__instruction">');
            print.document.write(`${step.instructions}`);
            print.document.write('</div>');
            print.document.write('<div class="osrm-print__section__duration">');
            print.document.write(`${m2m(step.distance)} / ${s2t(step.duration)}`);
            print.document.write('</div>');
            print.document.write('</div>');
          });
        });
        print.document.write('</body></html>');
        print.document.close();
        print.focus();
        print.print();
        print.close();
      },
      getStepClass(type) {
        return `leaflet-map-osrm__content__scroll__leg__step leaflet-map-osrm__content__scroll__leg__step--${type}`;
      },
      getInstructions(args) {
        Object.keys(args).forEach(key => args[key] = this.clean(args[key]));
        const {type, modifier, name, destinations, ref, exits} = args;
        switch (type) {
          case 'arrive':
            return `You have arrived at your destination ${modifier == 'straight' ? 'ahead' : `on the ${modifier}`}`;
          case 'close':
            return 'Road will close';
          case 'continue':
            return `Continue ${modifier} ${notEmpty(ref) ? `on ${ref}` : ''} ${notEmpty(name) ? `on ${name}` : ''}`;
          case 'depart':
            return `Depart ${modifier} ${notEmpty(ref) ? `on ${ref}` : ''} ${notEmpty(name) ? `on ${name}` : ''}`;
          case 'end of road':
            return `Turn ${modifier} at the end of the road`;
          case 'fork':
            return `${modifier === 'straight' ? 'Continue': 'Turn'} ${modifier} at the fork
            ${notEmpty(name) ? `on ${name}` : ''} ${notEmpty(destinations) ? `to ${destinations}` : ''}`;
          case 'merge':
            return `Merge ${modifier} ${notEmpty(ref) ? `on ${ref}` : ''} ${notEmpty(name) ? `on ${name}` : ''}`;
          case 'new name':
          case 'notification':
          case 'turn':
          case 'invalid':
            return `${modifier === 'straight' ? 'Continue': 'Turn'} ${modifier}
            ${notEmpty(ref) ? `on ${ref}` : ''} ${notEmpty(name) ? `on ${name}` : ''}`;
          case 'off ramp':
          case 'on ramp':
            return `Take the ${type === 'off ramp' ? 'off ramp' : 'on ramp'}
            ${modifier} ${notEmpty(exits) ? `exits ${exits}` : ''}
            ${notEmpty(destinations) ? `to ${destinations}` : ''}`;
          case 'rotary':
          case 'roundabout':
            return `In the ${type === 'rotary' ? 'rotary' : 'roundabout'}
            ${modifier === 'straight' ? 'continue': 'turn'} ${modifier}
            ${notEmpty(ref) ? `on ${ref}` : ''} ${notEmpty(name) ? `on ${name}` : ''}`;
          case 'uturn':
            return 'Perform a u-turn';
        }
      },
      clean(val) {
        return typeof val === 'string' ? val.replace(/\$/g, '') : val;
      },
      getDirectionIcon(args) {
        const {type, modifier} = args;
        const part1 = type;
        const part2 = notEmpty(modifier) ? `_${modifier}` : '';
        return `${part1}${part2}`.replace(/\s/g, '_');
      },
      handleDestroyThis() {
        this.$nextTick(() => {
          this.$destroy();
          this.$el.parentNode.removeChild(this.$el);
        });
      },
    },
    watch: {
      geometry: {
        handler: async function(value) {
          this.$emit('showPolyline', value);
        },
        immediate: true,
      },
      wayPointBlob: {
        handler: async function(value) {
          const vm = this;
          if (notEmpty(value)) {
            vm.routeData = await osrm
              .get(`${value}?overview=full&alternatives=true&steps=true&continue_straight=true`)
              .then(res => res.data)
              .then(data => {
                console.log(data);
                if (!has(data, 'routes')) {
                  return vm.handleDestroyThis();
                }
                return data;
              })
              .catch(() => vm.handleDestroyThis());
          }

          // vm.routeData = TEST_DATA;

        },
        immediate: true,
        deep: true,
      },
    },
  });
</script>
